import api from './api';

// All Cognito traffic goes through the Node server at /aws-auth. The browser
// never holds the app client id or any Cognito token; the express session
// cookie is the only credential. Errors are normalised to { code, message }
// so callers can keep matching on Cognito error codes (see constants.awsCodes).

const AWS_AUTH = '/aws-auth';

const awsRequest = async (options) => {
  try {
    const response = await api.request({ ...options, url: `${AWS_AUTH}${options.url}` });
    return response.data;
  } catch (e) {
    const data = (e.response && e.response.data) || {};
    const error = new Error(data.message || e.message || 'Request failed.');
    error.code = data.code;
    error.status = e.response && e.response.status;
    throw error;
  }
};

export const validateSession = async (token) => {
  const response = await api.request({
    method: 'get',
    url: '/session-token',
    params: { token }
  });
  return response.data;
};

export const logoutSession = async () => {
  const response = await api.request({
    method: 'post',
    url: '/session-token/log-out'
  });
  return response.data;
};

// Accepts the shape the sign-up form already builds:
// { username, password, options: { userAttributes: { email, given_name, ... } } }
export const awsSignUp = async (user) => {
  const userAttributes = (user.options && user.options.userAttributes) || {};
  return awsRequest({
    method: 'post',
    url: '/sign-up',
    data: {
      username: user.username,
      password: user.password,
      captchaToken: user.captchaToken,
      attributes: Object.entries(userAttributes).map(([Name, Value]) => ({ Name, Value }))
    }
  });
};

export const awsConfirmSignUp = async (user, code) => awsRequest({
  method: 'post',
  url: '/sign-up/confirm',
  data: { username: user, code }
});

// Resolves to { signedIn: true } or { challengeName, challengeParameters }.
export async function signInAWS (user, password) {
  return awsRequest({
    method: 'post',
    url: '/sign-in',
    data: { username: user, password }
  });
}

export const signInIQ = async (user) => {
  user.email = user.email?.toLowerCase();
  const response = await api.request({
    method: 'post',
    url: '/user/',
    data: user
  });
  return response.data;
};

// Signed-in Cognito user: { username, attributes: {...}, mfa: { enabled, preferred } }.
// Rejects with status 401 when there is no server-side Cognito session.
export const getAwsUser = async () => awsRequest({
  method: 'get',
  url: '/me'
});

export const getExistingIQUser = async (email, getIqUrl) => {
  const response = await api.request({
    method: 'get',
    url: '/user/',
    params: { email, iqUrl: getIqUrl }
  });
  return response.data;
};

// Resolves to { destination: { Destination, DeliveryMedium, AttributeName } }.
export const resendAwsVerify = async (user) => awsRequest({
  method: 'post',
  url: '/sign-up/resend',
  data: { username: user }
});

export const resendIQMFA = async (userId) => {
  const response = await api.request({
    method: 'post',
    url: '/user/challenge',
    params: { userId, pinCodeLength: 4 }
  });
  return response.data;
};

// Answers the MFA challenge Cognito issued at sign-in.
export const validateAwsChallenge = async (code) => awsRequest({
  method: 'post',
  url: '/challenge',
  data: { code }
});

export const validateIQChallenge = async (userId, code, challengeId) => {
  const response = await api.request({
    method: 'post',
    url: '/user/validate',
    params: { userId, pin: code, challengeId }
  });
  return response.data;
};

export const awsSignout = async () => awsRequest({
  method: 'post',
  url: '/sign-out'
});

export const awsForgotPassword = async (email) => awsRequest({
  method: 'post',
  url: '/forgot-password',
  data: { username: email }
});

export const awsConfirmForgotPassword = async (email, code, newPassword) => awsRequest({
  method: 'post',
  url: '/forgot-password/confirm',
  data: { username: email, code, password: newPassword }
});

export const awsChangePassword = async (oldPassword, newPassword) => awsRequest({
  method: 'put',
  url: '/password',
  data: { oldPassword, newPassword }
});

// method: 'sms' | 'email'
export const awsSetMfaPreference = async (method) => awsRequest({
  method: 'put',
  url: '/mfa-preference',
  data: { method }
});

// attributes: { phone_number: '+1...' }
export const awsUpdateAttributes = async (attributes) => awsRequest({
  method: 'put',
  url: '/attributes',
  data: { attributes }
});

export const logUser = async (email) => {
  let ipAddress = '0.0.0.0';

  try {
      const ipData = await api.request({
          method: 'get',
          url: 'https://api.ipify.org/?format=json'
      });

      ipAddress = (ipData && ipData.data && ipData.data.ip) ? String(ipData.data.ip) : '0.0.0.0';
  } catch (err) {
      console.warn('ipify failed:', err);
  }

  return api.request({
      method: 'get',
      url: '/user/login-log',
      params: {
          email,
          appId: 'GW',
          ipAddress,
      },
  });
};
